import { seedExercises, extraWords } from "./seeds.js";
import { chars, count, fold, isLetter, isNumber, isUppercase, isWhitespace, splitSpaces, trimWSNL } from "./text.js";

const expansions: Record<string, string> = {
  db: "dumbbell",
  bb: "barbell",
  kb: "kettlebell",
  dl: "deadlift",
  tbdl: "trap bar deadlift",
  sldl: "single leg deadlift",
  rdl: "romanian deadlift",
  ohp: "overhead press",
  sl: "single leg",
  bw: "bodyweight",
  ghr: "glute ham raise",
  cgbp: "close grip bench press",
  dumbell: "dumbbell",
  dumbel: "dumbbell",
  kettelbell: "kettlebell",
  pullup: "pull up",
  pushup: "push up",
  chinup: "chin up",
  situp: "sit up",
  stepup: "step up",
};

const joiners = new Set([
  "from", "to", "into", "onto", "with", "on", "in", "at", "off", "per",
  "behind", "under", "over", "across", "around", "against", "and",
]);

const separators = new Set(["-", "_", "/", "–", "—"]);
const invariantWords = new Set(["press", "triceps", "biceps", "abs", "lats", "delts", "pecs", "quads"]);

function expansion(word: string): string | undefined {
  const direct = expansions[word];
  if (direct !== undefined) return direct;
  if (!word.endsWith("s")) return undefined;
  return expansions[singular(word)];
}

export function singular(word: string): string {
  if (invariantWords.has(word) || chars(word).some(isNumber)) return word;
  const n = count(word);
  if (!word.endsWith("s") || n <= 2) return word;
  if (word.endsWith("ies") && n > 4) return chars(word).slice(0, n - 3).join("") + "y";
  for (const ending of ["sses", "ches", "shes", "xes", "zes"]) {
    if (word.endsWith(ending)) return chars(word).slice(0, n - 2).join("");
  }
  if (word.endsWith("ss")) return word;
  return chars(word).slice(0, n - 1).join("");
}

function scalars(name: string): string {
  let out = "";
  for (const c of fold(name).toLowerCase()) {
    if (isLetter(c) || isNumber(c)) out += c;
    else if (isWhitespace(c) || separators.has(c)) out += " ";
  }
  return out;
}

function normalize(name: string, expanding: boolean): string {
  let words = splitSpaces(scalars(name));
  if (expanding) {
    words = words.flatMap((word) => {
      const expanded = expansion(word);
      return expanded === undefined ? [word] : splitSpaces(expanded);
    });
  }
  if (words.length === 0) return "";
  for (let i = 0; i < words.length - 1; i += 1) {
    if (joiners.has(words[i + 1]!)) words[i] = singular(words[i]!);
  }
  words[words.length - 1] = singular(words[words.length - 1]!);
  return words.join(" ");
}

const keys = new Map<string, string>();

/** `ExerciseKey(name).rawValue`. */
export function exerciseKey(name: string): string {
  let key = keys.get(name);
  if (key === undefined) {
    key = normalize(name, true);
    if (keys.size > 50_000) keys.clear();
    keys.set(name, key);
  }
  return key;
}

export function literalKey(name: string): string {
  return normalize(name, false);
}

export function plainKey(name: string): string {
  return splitSpaces(scalars(name)).join(" ");
}

const seedKeys = seedExercises.map(exerciseKey);
const seedKeySet = new Set(seedKeys);

export function isKnownName(name: string, known: readonly string[]): boolean {
  const key = exerciseKey(name);
  return known.some((k) => exerciseKey(k) === key) || seedKeySet.has(key);
}

export function editDistance(a: string, b: string, limit = Number.MAX_SAFE_INTEGER): number {
  const left = chars(a);
  const right = chars(b);
  if (left.length === 0) return right.length;
  if (right.length === 0) return left.length;
  let previous = Array.from({ length: right.length + 1 }, (_, i) => i);
  let current = previous.slice();
  for (let i = 1; i <= left.length; i += 1) {
    current[0] = i;
    let rowBest = current[0]!;
    for (let j = 1; j <= right.length; j += 1) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      current[j] = Math.min(previous[j]! + 1, current[j - 1]! + 1, previous[j - 1]! + cost);
      rowBest = Math.min(rowBest, current[j]!);
    }
    if (rowBest > limit) return limit + 1;
    [previous, current] = [current, previous];
  }
  return previous[right.length]!;
}

/** `8-step take-off` is one letter from `6-step take-off` and another drill. */
function numbers(key: string): string {
  return (key.match(/\d+/g) ?? []).join(" ");
}

function closest(key: string, candidates: readonly string[], candidateKeys?: readonly string[]): string | null {
  let best: { name: string; key: string; distance: number } | null = null;
  let tied = false;
  const keyCount = count(key);
  candidates.forEach((candidate, index) => {
    const candidateKey = candidateKeys?.[index] ?? exerciseKey(candidate);
    const candidateCount = count(candidateKey);
    const limit = Math.min(2, Math.trunc(0.2 * Math.max(keyCount, candidateCount)));
    if (limit <= 0 || Math.abs(candidateCount - keyCount) > limit || numbers(candidateKey) !== numbers(key)) return;
    const distance = editDistance(key, candidateKey, limit);
    if (distance > limit) return;
    if (best === null) {
      best = { name: candidate, key: candidateKey, distance };
      return;
    }
    if (distance < best.distance) {
      best = { name: candidate, key: candidateKey, distance };
      tied = false;
    } else if (distance === best.distance && candidateKey !== best.key) {
      tied = true;
    }
  });
  const found = best as { name: string } | null;
  return tied ? null : (found?.name ?? null);
}

function closestWord(word: string, vocabulary: string[]): string | null {
  const wordCount = count(word);
  const limit = Math.min(2, Math.trunc(0.15 * wordCount));
  if (limit <= 0) return null;
  let best: { word: string; distance: number } | null = null;
  let tied = false;
  for (const candidate of vocabulary) {
    if (Math.abs(count(candidate) - wordCount) > limit) continue;
    const distance = editDistance(word, candidate, limit);
    if (distance > limit) continue;
    if (best === null) {
      best = { word: candidate, distance };
      continue;
    }
    if (distance < best.distance) {
      best = { word: candidate, distance };
      tied = false;
    } else if (distance === best.distance) {
      tied = true;
    }
  }
  return tied ? null : (best?.word ?? null);
}

function compareStrings(a: string, b: string): number {
  const x = chars(a).map((c) => c.codePointAt(0)!);
  const y = chars(b).map((c) => c.codePointAt(0)!);
  for (let i = 0; i < Math.min(x.length, y.length); i += 1) {
    if (x[i] !== y[i]) return x[i]! - y[i]!;
  }
  return x.length - y.length;
}

let seedVocabulary: { words: Set<string>; sorted: string[] } | null = null;

function vocabulary(known: readonly string[]): { words: Set<string>; sorted: string[] } {
  if (seedVocabulary === null) {
    const words = new Set(extraWords);
    for (const name of seedExercises) for (const word of splitSpaces(literalKey(name))) words.add(word);
    seedVocabulary = { words, sorted: [...words].sort(compareStrings) };
  }
  const extra = new Set<string>();
  for (const name of known) {
    for (const word of splitSpaces(literalKey(name))) if (!seedVocabulary.words.has(word)) extra.add(word);
  }
  if (extra.size === 0) return seedVocabulary;
  const words = new Set([...seedVocabulary.words, ...extra]);
  const added = [...extra].sort(compareStrings);
  const sorted: string[] = [];
  let i = 0;
  let j = 0;
  const base = seedVocabulary.sorted;
  while (i < base.length || j < added.length) {
    if (j >= added.length || (i < base.length && compareStrings(base[i]!, added[j]!) < 0)) sorted.push(base[i++]!);
    else sorted.push(added[j++]!);
  }
  return { words, sorted };
}

function matchingCase(original: string, replacement: string): string {
  const head = chars(replacement)[0];
  if (!isUppercase(chars(original)[0]) || head === undefined) return replacement;
  return head.toUpperCase() + chars(replacement).slice(1).join("");
}

function fixWords(name: string, known: readonly string[]): string {
  const { words, sorted } = vocabulary(known);
  const out: string[] = [];
  let changed = false;
  for (const original of name.split(" ")) {
    const normalized = plainKey(original);
    const replacement =
      count(normalized) >= 6 && !normalized.includes(" ") && !words.has(normalized) && !words.has(singular(normalized))
        ? closestWord(normalized, sorted)
        : null;
    if (replacement === null) {
      out.push(original);
      continue;
    }
    out.push(matchingCase(original, replacement));
    changed = true;
  }
  return changed ? out.join(" ") : name;
}

/** `NameMatcher.resolve`. */
export function resolveName(name: string, known: readonly string[] = []): string {
  const trimmed = trimWSNL(name);
  const key = exerciseKey(trimmed);
  if (key === "") return trimmed;
  const match = known.find((k) => exerciseKey(k) === key);
  if (match !== undefined) return match;
  const seedIndex = seedKeys.indexOf(key);
  if (seedIndex >= 0) {
    const seed = seedExercises[seedIndex]!;
    return literalKey(seed) === literalKey(trimmed) ? trimmed : seed;
  }
  return closest(key, known) ?? closest(key, seedExercises, seedKeys) ?? fixWords(trimmed, known);
}
